import React from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingOverlay from '@/common/LoadingOverlay/LoadingOverlay';
import SuperAdminAccessDenied, {
  type SuperAdminAccessDeniedReason,
} from '@/pages/super-admin-access-denied';
import { ProfileProvider } from '@/ProfileContext';
import { SuperAdminIdentityProvider } from '@/contexts/SuperAdminIdentityContext';
import { buildAuth0AuthorizationParams } from '@/lib/auth0AuthorizationParams';
import {
  checkSuperAdminAccess,
  type SuperAdminAccessCheckResult,
  type SuperAdminMeIdentity,
} from '@/services/superAdminAccessService';

type GatePhase =
  | { kind: 'loading' }
  | { kind: 'approved'; identity: SuperAdminMeIdentity }
  | { kind: 'blocked'; reason: SuperAdminAccessDeniedReason; detailMessage?: string };

function phaseFromResult(result: SuperAdminAccessCheckResult): GatePhase {
  if (result.status === 'approved') {
    return { kind: 'approved', identity: result.identity };
  }
  if (result.status === 'denied') {
    return {
      kind: 'blocked',
      reason: result.httpStatus === 401 ? 'denied' : 'pending',
    };
  }
  if (result.status === 'unavailable') {
    return { kind: 'blocked', reason: 'unavailable' };
  }
  return {
    kind: 'blocked',
    reason: 'error',
    detailMessage: result.message,
  };
}

/**
 * Hard gate after Auth0 login: only users with an active kk-super-admin-user
 * row (verified via GET /sa/super-admin/me) may render the app shell.
 */
const SuperAdminAccessGate: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [phase, setPhase] = React.useState<GatePhase>({ kind: 'loading' });
  const checkGeneration = React.useRef(0);

  const runCheck = React.useCallback(async () => {
    const generation = ++checkGeneration.current;
    setPhase({ kind: 'loading' });

    const getToken = () =>
      getAccessTokenSilently({
        authorizationParams: buildAuth0AuthorizationParams(),
      });

    const result = await checkSuperAdminAccess(getToken);
    if (generation !== checkGeneration.current) {
      return;
    }
    setPhase(phaseFromResult(result));
  }, [getAccessTokenSilently]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    void runCheck();
  }, [isAuthenticated, runCheck]);

  if (phase.kind === 'loading') {
    return <LoadingOverlay />;
  }

  if (phase.kind === 'blocked') {
    return (
      <SuperAdminAccessDenied
        reason={phase.reason}
        detailMessage={phase.detailMessage}
        onRetry={phase.reason === 'error' ? () => void runCheck() : undefined}
      />
    );
  }

  return (
    <SuperAdminIdentityProvider identity={phase.identity}>
      <ProfileProvider>
        <Outlet />
      </ProfileProvider>
    </SuperAdminIdentityProvider>
  );
};

export default SuperAdminAccessGate;
